import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Zap, Heart, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { fmt, errMsg, mediaUrl } from "../lib/api";
import { useAuth, useCart, useWishlist } from "../context/Store";
import { useNavigate } from "react-router-dom";

export default function ProductCard({ product, tall = false }) {
  const { user } = useAuth();
  const { add } = useCart();
  const { wishlistIds, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const wished = wishlistIds.includes(product.id);
  const galleryRef = useRef(null);
  const [activeImage, setActiveImage] = useState(0);
  const images = product.images?.length ? product.images : [""];

  const showImage = (index, event) => {
    event.preventDefault();
    event.stopPropagation();
    const nextIndex = (index + images.length) % images.length;
    galleryRef.current?.children[nextIndex]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    setActiveImage(nextIndex);
  };

  const quickAdd = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/auth?next=/shop");
      return;
    }
    try {
      await add(product.id, 1);
      toast.success(`${product.name} added to cart`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggleWish = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/auth?next=/shop");
      return;
    }
    try {
      const added = await toggleWishlist(product.id);
      toast.success(added ? `${product.name} saved to wishlist` : `${product.name} removed from wishlist`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link to={`/product/${product.slug}`} data-testid={`product-card-${product.slug}`} className="group block">
        <div className={`relative overflow-hidden rounded-2xl bg-[#F4F4F5] ${tall ? "aspect-[3/4]" : "aspect-square"}`}>
          <div ref={galleryRef} onScroll={(event) => setActiveImage(Math.round(event.currentTarget.scrollLeft / event.currentTarget.clientWidth))}
            className="flex h-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {images.map((image, index) => <img key={`${image}-${index}`} src={mediaUrl(image)} alt={`${product.name} image ${index + 1}`} loading="lazy"
              className="h-full w-full shrink-0 snap-start object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" />)}
          </div>
          {images.length > 1 && <>
            <button type="button" aria-label="Previous product image" onClick={(event) => showImage(activeImage - 1, event)} className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-ink shadow opacity-0 transition group-hover:opacity-100"><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" aria-label="Next product image" onClick={(event) => showImage(activeImage + 1, event)} className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-ink shadow opacity-0 transition group-hover:opacity-100"><ChevronRight className="h-4 w-4" /></button>
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/25 px-2 py-1.5" aria-label={`Image ${activeImage + 1} of ${images.length}`}>
              {images.map((image, index) => <span key={`${image}-dot-${index}`} className={`h-1.5 w-1.5 rounded-full ${index === activeImage ? "bg-white" : "bg-white/50"}`} />)}
            </div>
          </>}
          {product.activation_eligible && (
            <span className="absolute top-3 left-3 flex items-center gap-1 rounded-full brand-gradient-bg px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
              <Zap className="h-3 w-3" /> Partner Kit
            </span>
          )}
          <button
            data-testid={`wishlist-toggle-${product.slug}`}
            onClick={toggleWish}
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 shadow flex items-center justify-center transition-transform hover:scale-110"
            aria-label="Save to wishlist"
          >
            <Heart className={`h-4 w-4 transition-colors ${wished ? "fill-rose-500 text-rose-500" : "text-brand-ink/50"}`} />
          </button>
          <button
            data-testid={`quick-add-${product.slug}`}
            onClick={quickAdd}
            className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-white shadow-lg flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hover:bg-brand-cyan"
            aria-label="Add to cart"
          >
            <Plus className="h-4 w-4 text-brand-ink" />
          </button>
        </div>
        <div className="pt-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-brand-ink/40 font-semibold">{product.brand}</p>
            <h3 className="font-display font-semibold text-brand-ink leading-snug mt-1">{product.name}</h3>
          </div>
          <div className="text-right shrink-0">
            <p className="font-display font-bold text-brand-ink">{fmt(product.price)}</p>
            {product.mrp > product.price && (
              <p className="text-xs text-brand-ink/40 line-through">{fmt(product.mrp)}</p>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
